import { create } from 'zustand'

import { useMapStateStore } from './core'
import { defaultBrowseFields } from './session'
import type { MapActionFailure } from './transitions/actions'
import { applyTransition } from './transitions/apply'

/**
 * How far the current URL has got towards being a place on the map.
 *
 * Arrival resolves against the catalog, so it takes a moment and can come back
 * with nothing: a link may name a stream that has since been deleted. Both are
 * states a page has to render — waiting, and landing on the fields list with
 * something said about why — and neither is a map position, so they live here
 * rather than on `MapState`.
 */
export type MapArrival =
  | { status: 'pending' }
  | { status: 'arrived' }
  | { status: 'failed'; failure: MapActionFailure }

const useMapArrivalStore = create<{ arrival: MapArrival }>(() => ({
  arrival: { status: 'pending' }
}))

export const useMapArrival = (): MapArrival => useMapArrivalStore((s) => s.arrival)

export const setMapArrival = (arrival: MapArrival) => useMapArrivalStore.setState({ arrival })

/**
 * What was asked for is not here — a link naming a stream that has been deleted,
 * or one deleted in another session while it was open.
 */
export const reportMapArrivalFailure = (failure: MapActionFailure) => {
  useMapStateStore.setState(
    applyTransition(useMapStateStore.getState(), {
      kind: 'ReturnToBrowseFields',
      session: defaultBrowseFields
    })
  )
  setMapArrival({ status: 'failed', failure })
}
